'use client';
import { useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useMediaQuery, useTheme } from '@mui/material';

import { useEventBookmarkAction, useMarketTradeModal } from '@/hooks';
import { useGetMarketDetails } from '@/services/query';
import { colors } from '@/styles/theme';
import type { Market, Outcome } from '@/types';
import { filterAndSortMarkets, getClobTokenId, normalizeMarketQuestion } from '@/utils/marketUtils';
import { useHistoricalChartData } from './useHistoricalChartData';
import { useMarketBuyHandlers } from './useMarketBuyHandlers';
import { useMarketSelection } from './useMarketSelection';

import { DEFAULT_SELECTED_RANGE, MarketTimeRange, ROUTES, TRADE_SIDES } from '@/constants';

const DEFAULT_COLORS = ['#e8b046', colors.info.main, '#4ec38a', '#c98a12'];

export const useMarketDetails = () => {
  const params = useParams();
  const marketId = params?.marketId as string;
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // --- 1. Data Fetching ---
  const { data: event, isLoading, error, refetch } = useGetMarketDetails(marketId);
  const sortedMarkets = event?.markets ? filterAndSortMarkets(event.markets) : [];
  const topMarkets = sortedMarkets.slice(0, 3);
  const market = (sortedMarkets[0] || event?.markets?.[0]) as unknown as Market;

  // --- 2. Shared Infrastructure ---
  const { tradeModalState, handleOpenTradeModal, handleCloseTradeModal } = useMarketTradeModal();
  const { checkIsBookmarked, handleBookmarkToggle } = useEventBookmarkAction();
  const isBookmarked = event ? checkIsBookmarked(event.id) : false;

  // --- 3. Domain Logic Hooks ---
  const [selectedRange, setSelectedRange] = useState<MarketTimeRange>(DEFAULT_SELECTED_RANGE);
  const [selectedSide, setSelectedSide] = useState<'yes' | 'no'>(TRADE_SIDES.YES);
  const [activeTab, setActiveTab] = useState<'buy' | 'sell'>('buy');

  const {
    selectedOutcomeId,
    setSelectedOutcomeId,
    effectiveSelectedOutcomeId,
    selectedOutcome,
    selectedMarket,
  } = useMarketSelection(event, sortedMarkets);

  const { handleBuyYesClick, handleBuyNoClick } = useMarketBuyHandlers({
    event,
    isMobile,
    setSelectedOutcomeId,
    setSelectedSide,
    handleOpenTradeModal,
    activeTab,
  });

  const { historicalData, isChartLoading } = useHistoricalChartData(topMarkets, selectedRange);

  // --- 4. Handlers ---
  const handleBookmarkClick = (e?: React.MouseEvent) => {
    if (!event) return;
    handleBookmarkToggle(e, event, 'market', ROUTES.MARKET_DETAILS(marketId));
  };

  const chartMarkets = topMarkets.map((m, idx) => ({
    id: m.id,
    name: normalizeMarketQuestion(m),
    color: DEFAULT_COLORS[idx % 4],
    marketId: getClobTokenId(m),
    clobTokenIds: (m as any).clobTokenIds,
    groupItemTitle: m.groupItemTitle,
    question: m.question,
  }));

  const totalVolume = event ? Number(event.volume || 0) : 0;

  return {
    state: {
      event,
      marketId,
      market: selectedMarket || market,
      isLoading,
      isChartLoading,
      error,
      selectedRange,
      selectedOutcomeId,
      effectiveSelectedOutcomeId,
      selectedOutcome,
      selectedMarket,
      tradeModalState,
      isBookmarked,
      historicalData,
      totalVolume,
      chartMarkets,
      isDesktop: !isMobile,
      isMobile,
      selectedSide,
      activeTab,
    },
    actions: {
      refetch,
      handleGoBack: () => router.back(),
      handleRangeChange: (range: string) => setSelectedRange(range as MarketTimeRange),
      createOutcomeClickHandler: (id: string) => () => setSelectedOutcomeId(id),
      handleBuyYesClick,
      handleBuyNoClick,
      handleMobileTrade: (outcome: Outcome, isBuyOrder: boolean) =>
        handleOpenTradeModal(selectedMarket as any, outcome as any, isBuyOrder),
      handleTradeModalClose: handleCloseTradeModal,
      handleBookmarkClick,
      getOutcomeColor: (_id: string, index: number) =>
        DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      setSelectedSide,
      setActiveTab,
    },
  };
};
